import classNames from 'classnames';
import { Button } from 'react-bootstrap';
import type { FunctionComponent } from 'react';

import { FREE_PLAN_NAME } from '../../../../../shared/subscriptions';
import { usePlanSelectorContext } from '../../../../contexts/PlanSelectorContext';
import { useRebuildUserRevision } from '../../../../hooks/api/revisions/useRebuildRevision';
import { isAuthed, trimString } from '../../../../utils';
import Icon, { icons } from '../../../generic/Icon';

import type { BuildCardProps } from './types';

type Props = Omit<BuildCardProps, 'isCLIBuild'>;

const AutomatedBuildCard: FunctionComponent<Props> = ({
  status,
  reference,
  defaultBranch,
  revisionId,
  revisionMessage,
  userCanEditProject,
  revisionLocator,
}) => {
  const { mutateAsync: rebuild, isPending: isRebuilding } = useRebuildUserRevision();
  const { showPlanSelectorModal } = usePlanSelectorContext();

  let statusMessage = 'Current Version';
  if (status === 'unanalyzed') {
    statusMessage = 'Not Analyzed:';
  } else if (reference !== defaultBranch) {
    statusMessage = trimString(reference, 18);
  }

  return (
    <div
      className={classNames(
        'project-card',
        'card',
        {
          'card-primary': status !== 'unanalyzed',
        },
      )}>
      <div className="row-fluid">
        <div className="col-md-1 project-card-gutter">
          <Icon icon={status === 'unanalyzed' ? icons.question : icons.building} />
        </div>
        <div className="col-md-8">
          <b className="inline-header">
            {statusMessage}
          </b>
          <h4>{revisionId}</h4>
          {revisionMessage && <h5>{revisionMessage}</h5>}
        </div>
        <div className="col-md-3 project-card-gutter">
          {status === 'unanalyzed' && (
            <Button
              bsStyle="success"
              disabled={
                isRebuilding ||
                !userCanEditProject
              }
              onClick={async () => {
                if (!isAuthed() && reference !== defaultBranch) {
                  // don't allow building of other branches
                  showPlanSelectorModal({ tier: FREE_PLAN_NAME });
                  return;
                }

                await rebuild({ revisionLocator });
              }}>
              <span className="margin-right-xs">
                {isRebuilding ? (
                  <Icon icon={icons.circleO} hasSpin />
                ) : (
                  <Icon icon={icons.search} />
                )}
              </span>Analyze this version
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutomatedBuildCard;
